import React, { useEffect, useRef, useState } from 'react'
import { MediaCategory, MediaWithCategories } from '../../../../types/Media'
import cx from 'classnames'
import styles from './MediaList.module.css'
import Loader from '../../../../components/Loader'
import CreateMediaCard from '../CreateMediaCard/CreateMediaCard'
import MediaCard from '../MediaCard/MediaCard'
import { useFetchData } from '../../../../hooks/useFetchData'
import { useFetchSingle } from '../../../../hooks/useFetchSingle'
import { api, ApiRoutes } from '../../../../services/ApiService'
import CategoryFilter from '../CategoryFilter/CategoryFilter'
import Pagination from '../../../../components/Pagination'
import ToggleSwitch from '../../../../components/ToggleSwitch'
import { useTranslation } from 'react-i18next'
import { showToast } from '../../../../services/ToastService'
import { GlobalSetting } from '../../../../types/GlobalSetting'

const SHOW_TITLE_IN_SHOP_NAME = 'show_media_image_title'

const MediaList: React.FC = () => {
    const { t } = useTranslation(['settings'])
    const currentPage = useRef<string>('1')
    const perPage = useRef<string>('13')

    const {
        data: media,
        fetch: fetchMedia,
        isLoading: isMediaLoading,
        pagination,
    } = useFetchData<MediaWithCategories>(ApiRoutes.MEDIA)

    const { data: categories, fetch: fetchCategories } =
        useFetchData<MediaCategory>(ApiRoutes.MEDIA_CATEGORIES)

    const {
        data: showTitleSetting,
        setData: setShowTitleSetting,
        fetch: fetchShowTitleSetting,
    } = useFetchSingle<GlobalSetting>(ApiRoutes.GLOBAL_SETTINGS)

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])

    const categoryIdsParam = selectedCategoryIds.join(',')

    useEffect(() => {
        currentPage.current = '1'
        fetchMedia({
            page: '1',
            perPage: perPage.current,
            categoryIds: categoryIdsParam,
        })
    }, [categoryIdsParam, fetchMedia])

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    useEffect(() => {
        fetchShowTitleSetting(SHOW_TITLE_IN_SHOP_NAME)
    }, [fetchShowTitleSetting])

    const showTitleInShop = showTitleSetting?.value === true

    const handleShowTitleChange = (_property: string, value: boolean) => {
        api.post(ApiRoutes.GLOBAL_SETTINGS, SHOW_TITLE_IN_SHOP_NAME, {
            name: SHOW_TITLE_IN_SHOP_NAME,
            value,
            input_type: 'boolean',
        })
            .then(() => {
                setShowTitleSetting(
                    showTitleSetting
                        ? { ...showTitleSetting, value }
                        : {
                              id: 0,
                              name: SHOW_TITLE_IN_SHOP_NAME,
                              value,
                              input_type: 'boolean',
                          }
                )
                showToast(t('overview.media.show_title_saved'))
            })
            .catch(() => {
                showToast(t('overview.media.show_title_error'), 'error')
            })
    }

    const changePage = (p: number) => {
        currentPage.current = p.toString()
        fetchMedia({
            page: p.toString(),
            perPage: perPage.current,
            categoryIds: categoryIdsParam,
        })
    }

    const refetch = () => {
        fetchMedia({
            page: currentPage.current,
            perPage: perPage.current,
            categoryIds: categoryIdsParam,
        })
        fetchCategories()
    }

    return (
        <>
             <ToggleSwitch
                property={SHOW_TITLE_IN_SHOP_NAME}
                value={showTitleInShop}
                label={t('overview.media.show_title_in_shop')}
                onChange={handleShowTitleChange}
                classes={cx(styles['media-list-toggle'])}
             />
             <CategoryFilter
                    categories={categories}
                    onChange={(ids: number[]) => setSelectedCategoryIds(ids)}
             />
            {!isMediaLoading && (
                <>
                    <div className={cx(styles['media-list'])}>
                        <CreateMediaCard onCreate={() => refetch()} />
                        {media.map((mediaElement) => {
                            return (
                                <MediaCard
                                    key={mediaElement.id}
                                    media={mediaElement}
                                    onEdit={() => refetch()}
                                    onDelete={() => refetch()}
                                    categories={categories}
                                />
                            )
                        })}
                    </div>
                    {pagination && pagination.lastPage > 1 && (
                        <Pagination
                            totalPages={pagination.lastPage}
                            currentPage={Number(currentPage.current)}
                            onPageChange={changePage}
                            classes={'zr-mt-4'}
                        />
                    )}
                </>
            )}
            {isMediaLoading && (
                <div className={cx(styles['loading-wrapper'])}>
                    <Loader isLoading={isMediaLoading} size={'m'} />
                </div>
            )}
        </>
    )
}

export default MediaList
